import { brandAssets } from '@echo/brand';
import appleMark from '@echo/brand/assets/apple-mark.svg';
import googleMark from '@echo/brand/assets/google-mark.svg';
import logo from '@echo/brand/assets/logo-lockup-goodwin.png';
import onearlyLogo from '@echo/brand/assets/onearly-logo.svg';
import {
  chooseHomeRegion,
  type IdentifierChannel,
  type LoginFlow,
  maskIdentifier,
  ONEARLY_PRIVACY_URL,
  otherRegions,
  readCoreErrorCode,
  regionName,
  type SignUpRegions,
  type SocialProvider,
  startSignIn,
  storedInMessage,
  TERMS_URL,
} from '@echo/core';
import { useAnalytics, useSession, useSessionManager, useSignUpRegions } from '@echo/core/react';
import { Button, Chip, TextField } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { OtpField } from '@/components/otp-field';
import { PhoneField, type PhoneValue } from '@/components/phone-field';
import { savePendingSocialLogin, socialRedirectUri, takePendingFlow } from '@/lib/social-login';
import { resetWelcome } from '@/lib/welcome-store';

export const Route = createFileRoute('/sign-in')({ component: SignInPage });

const CODE_LENGTH = 6;

/**
 * "Let's get started" and "Confirm your details", in
 * OnEarly's own purple because they are the "Powered by OnEarly" screens. Core brokers the
 * Ory flows exactly as the OnEarly app does — one-time code by phone/email (password when
 * Core says so), Apple/Google via a same-tab redirect to `/auth/callback`, and account
 * linking when the provider's email already belongs to an OnEarly account.
 */
function SignInPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const session = useSession();
  const sessionManager = useSessionManager();
  const [pendingLink] = useState(takePendingFlow);
  const [channel, setChannel] = useState<IdentifierChannel>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<PhoneValue>({ e164: '', valid: false });
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [flow, setFlow] = useState<LoginFlow | undefined>(pendingLink?.flow);
  const [linking, setLinking] = useState<SocialProvider | undefined>(pendingLink?.provider);
  // A new account's code screen says where its data will be stored, and tucks the other open
  // regions away behind "Wrong region?" (`region.ts`).
  const regions = useSignUpRegions(navigator.languages[0]);
  const [homeRegions, setHomeRegions] = useState<SignUpRegions | undefined>();
  const [choosingRegion, setChoosingRegion] = useState(false);
  // "Store my data in a different region": focus comes back to it once a region is picked.
  const regionLink = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  // Which provider the browser is on its way to: the button keeps its spinner until the page
  // has actually left, so a tap on Apple or Google is never met with a button that just
  // springs back while the redirect is still being set up.
  const [opening, setOpening] = useState<SocialProvider | undefined>();
  const [error, setError] = useState<string | undefined>();
  // Keys the code field: bumped when the code is cleared for a retry so it refocuses.
  const [attempt, setAttempt] = useState(0);
  const sentTo = flow ? maskIdentifier(flow.channel, flow.identifier) : '';
  const identifier = channel === 'phone' ? phone.e164 : email.trim();
  const canContinue = channel === 'phone' ? phone.valid : email.includes('@');

  // Being here means signed out: the next sign-in starts with Brian's welcome again.
  useEffect(() => {
    if (session.status !== 'unknown' && session.status !== 'authenticated') resetWelcome();
  }, [session.status]);

  function describe(e: unknown): string {
    switch (readCoreErrorCode(e)) {
      case 'INVALID_CODE':
        return t`That code isn't right. Check it and try again.`;
      case 'FLOW_EXPIRED':
        return t`That code has expired. Ask for a new one.`;
      case 'SOCIAL_LINK_UNSUPPORTED':
        return t`That account can't be linked here yet. Sign in with your phone or email instead.`;
      case 'AUTH_FLOW_ERROR':
        return t`We couldn't start signing you in. Please try again.`;
      default:
        return e instanceof Error && !readCoreErrorCode(e) && e.message === 'Invalid code'
          ? t`That code isn't right. Check it and try again.`
          : t`Something went wrong`;
    }
  }

  async function run(task: () => Promise<void>): Promise<boolean> {
    setBusy(true);
    setError(undefined);
    try {
      await task();
      return true;
    } catch (e) {
      setError(describe(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const finish = async (
    tokens: Awaited<ReturnType<typeof sessionManager.provider.verifyLogin>>,
  ) => {
    await sessionManager.signIn(tokens);
    analytics.track({ name: 'sign_in_completed' });
    await navigate({ to: '/' });
  };

  const clearCodeForRetry = () => {
    setCode('');
    setAttempt((n) => n + 1);
  };

  const verify = (entered = code) =>
    run(async () => {
      if (!flow || entered.length !== CODE_LENGTH) return;
      try {
        await finish(await sessionManager.provider.verifyLogin({ flow, code: entered }));
      } catch (e) {
        clearCodeForRetry();
        throw e;
      }
    });

  /**
   * A pasted or autofilled code arrives whole and verifies on its own; a typed sixth digit
   * waits for Continue so a typo doesn't fire a request before it can be read back.
   */
  const onCodeChange = (next: string) => {
    const arrivedAtOnce =
      next.length - code.length > 1 ||
      (code.length === CODE_LENGTH && next.length === CODE_LENGTH && next !== code);
    setCode(next);
    if (next.length === CODE_LENGTH && arrivedAtOnce) void verify(next);
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    void run(async () => {
      if (!flow) {
        analytics.track({ name: 'sign_in_started', channel });
        // `/login/start` doubles as sign-up: an unknown email or phone comes back as a
        // `registration` flow with its code already sent, and `verify` creates the account.
        // The rest of the profile (name) is collected once signed in, as OnEarly `app` does.
        // For an account with no home yet the flow carries the pre-selected region, which the
        // code screen shows and goes with the code to verify (`region.ts`).
        const started = await startSignIn({
          provider: sessionManager.provider,
          resolveRegions: regions.resolve,
          channel,
          identifier,
        });
        setHomeRegions(started.regions);
        setFlow(started.flow);
      } else if (flow.authMethod === 'password') {
        await finish(
          await sessionManager.provider.passwordLogin({ identifier: flow.identifier, password }),
        );
      } else {
        await verify();
      }
    });
  }

  const resend = () =>
    run(async () => {
      if (!flow) return;
      setFlow(await sessionManager.provider.resendLogin(flow));
      clearCodeForRetry();
    });

  /** A region picked under "Wrong region?": the line says it, and it goes with the code. */
  const pickRegion = (code: string) => {
    setFlow((current) => (current ? chooseHomeRegion(current, code) : current));
    setChoosingRegion(false);
    // The pressed choice goes away with the others; the link stays, and Tab goes on to the code.
    regionLink.current?.focus();
  };

  const social = async (provider: SocialProvider) => {
    setOpening(provider);
    const started = await run(async () => {
      analytics.track({ name: 'sign_in_started', channel: provider });
      const login = await sessionManager.provider.startSocialLogin({
        provider,
        redirectUri: socialRedirectUri(),
      });
      savePendingSocialLogin({
        provider,
        sessionTokenExchangeCode: login.sessionTokenExchangeCode,
      });
      window.location.assign(login.url);
    });
    // Only a failed start lands here; otherwise the page is leaving for the provider.
    if (!started) setOpening(undefined);
  };

  const reset = () => {
    setFlow(undefined);
    setLinking(undefined);
    setHomeRegions(undefined);
    setChoosingRegion(false);
    setCode('');
    setPassword('');
    setError(undefined);
  };

  if (session.status === 'authenticated') return <Navigate to="/" replace />;

  // Where a new account's data will be stored, and where else it could be (`region.ts`).
  const storedIn = homeRegions ? storedInMessage(homeRegions, flow?.homeRegion) : undefined;
  const elsewhere = homeRegions ? otherRegions(homeRegions, flow?.homeRegion) : [];
  const nameOf = (option: { code: string; label: string }) => {
    const name = regionName(option.code);
    return name ? t(name) : option.label;
  };

  const poweredBy = (
    <p className="flex items-center justify-center gap-internal-md py-content-sm text-body-xs">
      <Trans>Powered by</Trans>
      <img src={onearlyLogo} alt="OnEarly" width={88} height={22} />
    </p>
  );

  return (
    <div className="min-h-dvh bg-surface">
      <main className="mx-auto flex w-full max-w-[480px] flex-col gap-content-lg px-content-md pt-section-sm pb-content-lg">
        <img
          src={logo}
          alt="Stronger Memory, powered by Goodwin Living Foundation"
          width={brandAssets.logoLockupGoodwin.width}
          height={brandAssets.logoLockupGoodwin.height}
          className="h-auto w-full max-w-[360px]"
        />
        <h1 className="text-headline-lg">
          {flow && flow.authMethod !== 'password' ? (
            linking ? (
              <Trans>Confirm it's you</Trans>
            ) : (
              <Trans>Confirm your details</Trans>
            )
          ) : (
            <Trans>Let's get started</Trans>
          )}
        </h1>
        {session.status === 'expired' ? (
          <p className="text-body-md text-on-surface-medium">
            <Trans>Your session has expired. Please sign in again.</Trans>
          </p>
        ) : null}

        {flow ? (
          flow.authMethod === 'password' ? (
            <form onSubmit={submit} className="flex flex-col gap-content-sm">
              <TextField
                label={t`Password for ${sentTo}`}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                type="password"
                autoComplete="current-password"
                autoFocus
              />
              <Button
                type="submit"
                loading={busy}
                variant="onearly"
                width="full"
                disabled={busy || password.length === 0}
              >
                <Trans>Sign in</Trans>
              </Button>
              <Button type="button" variant="ghost" width="full" onClick={reset} disabled={busy}>
                <Trans>Use a different account</Trans>
              </Button>
            </form>
          ) : (
            <>
              <div className="flex flex-col gap-internal-md">
                <p className="text-body-md">
                  {linking === 'apple' ? (
                    <Trans>
                      You already have an OnEarly account for <strong>{sentTo}</strong>. Enter the
                      6-digit code we just sent there to sign in and add Apple to your account.
                    </Trans>
                  ) : linking === 'google' ? (
                    <Trans>
                      You already have an OnEarly account for <strong>{sentTo}</strong>. Enter the
                      6-digit code we just sent there to sign in and add Google to your account.
                    </Trans>
                  ) : (
                    <Trans>
                      We’ve sent a 6-digit code to
                      <br />
                      <strong>{sentTo}</strong>
                    </Trans>
                  )}
                </p>
                {/* Above the code field, in view while the keyboard is up, rather than below it
                    where a pasted code could confirm a region the person never scrolled to. */}
                {storedIn ? (
                  <div className="flex flex-col">
                    <p className="text-body-xs text-on-surface-weak">
                      {t(storedIn)}
                      {elsewhere.length > 0 ? (
                        <>
                          {' '}
                          <Trans>Wrong region?</Trans>{' '}
                          <button
                            ref={regionLink}
                            type="button"
                            onClick={() => setChoosingRegion((open) => !open)}
                            disabled={busy}
                            aria-expanded={choosingRegion}
                            className="cursor-pointer text-link-onearly underline"
                          >
                            <Trans>Store my data in a different region</Trans>
                          </button>
                        </>
                      ) : null}
                    </p>
                    {choosingRegion ? (
                      <div className="flex flex-wrap gap-content-sm">
                        {elsewhere.map((option) => (
                          <button
                            key={option.code}
                            type="button"
                            onClick={() => pickRegion(option.code)}
                            disabled={busy}
                            className="min-h-tap-target min-w-tap-target cursor-pointer text-start text-body-sm text-link-onearly underline"
                          >
                            {nameOf(option)}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <form onSubmit={submit} className="flex flex-col gap-content-md">
                <div className="flex flex-col gap-internal-md">
                  <p className="text-label-sm">
                    <Trans>Enter the code to continue.</Trans>
                  </p>
                  <OtpField
                    key={attempt}
                    value={code}
                    onChange={onCodeChange}
                    disabled={busy}
                    accessibilityLabel={t`6-digit verification code`}
                  />
                  <p className="text-body-xs text-on-surface-weak">
                    <Trans>Didn't get the code?</Trans>{' '}
                    <button
                      type="button"
                      onClick={resend}
                      disabled={busy}
                      className="cursor-pointer text-link-onearly underline"
                    >
                      <Trans>Resend code</Trans>
                    </button>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  disabled={busy}
                  className="self-start cursor-pointer text-body-sm text-link-onearly underline"
                >
                  <Trans>Change phone number or email</Trans>
                </button>
                <div className="flex flex-col gap-content-sm">
                  <Button
                    type="submit"
                    loading={busy}
                    variant="onearly"
                    width="full"
                    disabled={busy || code.length !== CODE_LENGTH}
                  >
                    <Trans>Continue</Trans>
                  </Button>
                  <p className="text-center text-body-xs text-on-surface-weak">
                    {flow.channel === 'email' ? (
                      <Trans>Wrong email?</Trans>
                    ) : (
                      <Trans>Wrong phone number?</Trans>
                    )}{' '}
                    <button
                      type="button"
                      onClick={reset}
                      disabled={busy}
                      className="cursor-pointer text-link-onearly underline"
                    >
                      <Trans>Try another method</Trans>
                    </button>
                  </p>
                </div>
              </form>
              {error ? (
                <p role="alert" className="text-body-sm text-status-error">
                  {error}
                </p>
              ) : null}
              {poweredBy}
            </>
          )
        ) : (
          <>
            <div className="flex flex-col gap-content-sm">
              <Button
                variant="social"
                size="sm"
                width="full"
                onClick={() => social('apple')}
                disabled={busy || opening !== undefined}
                loading={opening === 'apple'}
              >
                {opening === 'apple' ? (
                  <Trans>Opening Apple…</Trans>
                ) : (
                  <>
                    <img src={appleMark} alt="" width={16} height={20} />
                    <Trans>Continue with Apple</Trans>
                  </>
                )}
              </Button>
              <Button
                variant="social"
                size="sm"
                width="full"
                onClick={() => social('google')}
                disabled={busy || opening !== undefined}
                loading={opening === 'google'}
              >
                {opening === 'google' ? (
                  <Trans>Opening Google…</Trans>
                ) : (
                  <>
                    <img src={googleMark} alt="" width={20} height={20} />
                    <Trans>Continue with Google</Trans>
                  </>
                )}
              </Button>
            </div>
            <div className="flex flex-col gap-internal-md">
              <p className="text-label-sm">
                <Trans>Or enter your email address or phone number.</Trans>
              </p>
              <fieldset className="flex flex-wrap gap-content-sm border-0 p-0">
                <legend className="sr-only">
                  <Trans>Sign in with</Trans>
                </legend>
                <Chip
                  tone="onearly"
                  pressed={channel === 'email'}
                  onPressedChange={() => setChannel('email')}
                >
                  {channel === 'email' ? <Check size={20} aria-hidden /> : null}
                  <Trans>Use email</Trans>
                </Chip>
                <Chip
                  tone="onearly"
                  pressed={channel === 'phone'}
                  onPressedChange={() => setChannel('phone')}
                >
                  {channel === 'phone' ? <Check size={20} aria-hidden /> : null}
                  <Trans>Use phone</Trans>
                </Chip>
              </fieldset>
            </div>
            {/* Figma Button group: field, Continue and the login line 16px apart. */}
            <form onSubmit={submit} className="flex flex-col gap-content-sm">
              {channel === 'phone' ? (
                <PhoneField label={t`Phone`} onChange={setPhone} disabled={busy} />
              ) : (
                <TextField
                  label={t`Email`}
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  type="email"
                  autoComplete="email"
                  placeholder={t({
                    message: 'you@example.com',
                    comment: 'Example email address in the input',
                  })}
                />
              )}
              <Button
                type="submit"
                loading={busy}
                variant="onearly"
                width="full"
                disabled={busy || !canContinue}
              >
                <Trans>Continue</Trans>
              </Button>
            </form>
            {error ? (
              <p role="alert" className="text-body-sm text-status-error">
                {error}
              </p>
            ) : null}
            {poweredBy}
            <p className="flex items-center justify-center gap-internal-md text-body-sm">
              <a href={TERMS_URL} className="text-link-onearly underline">
                <Trans>Terms</Trans>
              </a>
              <span className="text-on-surface-medium">•</span>
              <a href={ONEARLY_PRIVACY_URL} className="text-link-onearly underline">
                <Trans>Privacy Policy</Trans>
              </a>
            </p>
          </>
        )}
      </main>
    </div>
  );
}
